import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  CityDeliveryMap,
  datelc,
  DateRange,
  extractDateRange,
  extractDefaultStoreId,
  extractNumber,
  HxDeliveryZoneService,
  HxReportService,
  HxStoreService,
  HxYandexInfoService,
  isoDate,
  OrderDeliveryPointResponse,
  StoreFullModel,
  toRouterQueryParams,
  YaPolygon
} from 'hx-services';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { HxCheckboxComponent, HxDateRangeSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
    selector: 'app-delivery-heat-count-map',
    templateUrl: './delivery-heat-count-map.component.html',
    styleUrls: ['./delivery-heat-count-map.component.css'],
    imports: [HxStoreSelectComponent, FormsModule, HxDateRangeSelectComponent, HxCheckboxComponent, HxLoaderComponent, NgIf, TranslocoPipe]
})
export class DeliveryHeatCountMapComponent implements OnInit, OnDestroy {
  store: StoreFullModel;
  cityDeliveryArea?: CityDeliveryMap;
  params: {
    storeId?: number;
    date?: DateRange,
    isHeatMap?: boolean
  } = {};
  isLoading = false;
  response: OrderDeliveryPointResponse = { orderInfos: [] };
  private $destroyed = new Subject<void>();

  constructor(
    private reportService: HxReportService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private zoneService: HxDeliveryZoneService,
    private storeService: HxStoreService,
    private yandexInfoService: HxYandexInfoService,
  ) {
  }

  ngOnInit() {
    this.aRoute.queryParamMap.pipe(takeUntil(this.$destroyed)).subscribe(async paramMap => {
      const asBooleanFn = (name: string) => paramMap.get(name) ? paramMap.get(name) === 'true' : undefined;
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(new Date()), to: isoDate(new Date())};
      this.params.storeId = extractNumber('storeId', paramMap) ?? extractDefaultStoreId();
      this.params.isHeatMap = asBooleanFn('isHeatMap');
      if (this.params.storeId) {
        this.store = await this.storeService.getFullStore(this.params.storeId);
        await this.loadCityDeliveryArea();
        await this.yandexInfoService.destroyMap();

        if (this.params.isHeatMap) {
          await this.loadClusterMap();
        } else {
          await this.loadHeatMap();
        }
      }
    });
  }

  ngOnDestroy() {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  async loadHeatMap() {
    this.response = { orderInfos: [] };
    this.isLoading = true;
    try {
      this.response = await this.storeService.getDeliveryOrderPoints(this.params.storeId, this.params.date.from, this.params.date.to);
      if (this.response && this.response.orderInfos && this.response.orderInfos.length > 0) {
        await this.yandexInfoService.showHeatMap(this.cityDeliveryArea, this.response.orderInfos);
      }
    } finally {
      this.isLoading = false;
    }
  }

  async loadClusterMap() {
    this.response = { orderInfos: [] };
    this.isLoading = true;
    try {
      this.response = await this.storeService.getDeliveryOrderPoints(this.params.storeId, this.params.date.from, this.params.date.to);
      if (this.response && this.response.orderInfos && this.response.orderInfos.length > 0) {
        await this.yandexInfoService.showClusterMap(this.cityDeliveryArea, this.response.orderInfos);
      }
    } finally {
      this.isLoading = false;
    }
  }

  async loadCityDeliveryArea() {
    this.zoneService.getStoresCoordinatesByCityId(this.store.city.id, this.store.brandId, datelc(this.params.date.from)).then(deliveryArea => {
      deliveryArea.deliveryMap.features
        .filter(feature => feature.geometry.type === 'Polygon')
        .forEach(feature => (feature.geometry as YaPolygon).coordinates[0].forEach(item => {
          const b = item[0];
          item[0] = item[1];
          item[1] = b;
        }));
      this.cityDeliveryArea = deliveryArea;
    });
  }
}
